import { GoogleMap } from "@react-google-maps/api";
import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { IoLocationSharp } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { FaMinus, FaPlus } from "react-icons/fa6";
import {
  MdMyLocation,
  MdOutlineKeyboardDoubleArrowDown,
  MdOutlineKeyboardDoubleArrowUp,
} from "react-icons/md";
import { toast } from "react-toastify";
import { getAdresFromLatLng } from "../../features/address/addressSlice";
import SmallSpinner from "../shared/spinners/SmallSpinner";
import smoothPanAndZoom from "../../helper/maps/smoothPanAndZoom";

const DEFAULT_CENTER = { lat: 39.068919, lng: 34.773721 };

const Container = styled.div`
  width: 100%;
  height: 100%;

  .map-container {
    width: 100%;
    height: 100%;
    min-height: 0;
    z-index: 5;

    &.dimmed .gm-style {
      filter: grayscale(0.25) brightness(0.95) contrast(0.98);
      transition: filter 0.2s ease;
    }

    .center-marker {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -100%);
      transition: transform 0.3s ease-in-out;
      font-size: 35px;
      z-index: 1;
    }

    .control-container-left,
    .control-container {
      position: absolute;
      top: 20px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .control-container-left {
      left: 20px;
    }

    .control-container {
      right: 20px;
    }

    .spinner-top {
      position: absolute;
      top: 12px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 3;
      display: flex;
      align-items: center;
      width: 33px;
      height: 33px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.9);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
      pointer-events: none;
    }

    .control-item {
      width: 35px;
      height: 35px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #fff;
      color: #5c5b5b;
      font-size: 1.4rem;
      font-weight: 600;
    }

    .control-item[disabled] {
      opacity: 0.5;
      pointer-events: none;
    }
  }
`;

const AbsoluteLocationMap = ({
  updatedLocationItem,
  setUpdatedLocationItem,
  mapRef,
  setSearchText,
}) => {
  const dispatch = useDispatch();
  const { mapType } = useSelector((state) => state.layout);
  const [isLoading, setIsLoading] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const isBusyRef = useRef(false);

  const currentCenter =
    updatedLocationItem?.locationData?.lat && updatedLocationItem?.locationData?.lng
      ? {
          lat: Number(updatedLocationItem.locationData.lat),
          lng: Number(updatedLocationItem.locationData.lng),
        }
      : DEFAULT_CENTER;

const onMapLoad = useCallback(
  (map) => {
    mapRef.current = map;
    map.setMapTypeId("hybrid");
  },
  [mapRef]
);

  useEffect(() => {
    isBusyRef.current = isBusy;
  }, [isBusy]);

  const zoomIn = () => {
    if (!mapRef.current) return;
    mapRef.current.setZoom((mapRef.current.getZoom() ?? 5) + 1);
  };

  const zoomOut = () => {
    if (!mapRef.current) return;
    mapRef.current.setZoom((mapRef.current.getZoom() ?? 5) - 1);
  };

  const resolveAddress = useCallback(
    async (lat, lng) => {
      const data = await dispatch(getAdresFromLatLng({ lat, lng })).unwrap();
      return data?.results?.[0]?.formatted_address ?? "";
    },
    [dispatch]
  );

  const onMapDragEnd = useCallback(async () => {
    if (!mapRef.current || isBusyRef.current) return;

    setIsBusy(true);
    setIsLoading(true);
    mapRef.current.setOptions({
      draggable: false,
      gestureHandling: "none",
      scrollwheel: false,
    });

    const { lat, lng } = mapRef.current.getCenter().toJSON();
    setUpdatedLocationItem((current) => ({
      ...current,
      locationData: { ...current.locationData, lat, lng },
    }));
    smoothPanAndZoom({ lat, lng }, 15, 200, mapRef);

    try {
      const googleAddress = await resolveAddress(lat, lng);
      setSearchText(googleAddress);
      setUpdatedLocationItem((current) => ({
        ...current,
        locationData: {
          ...current.locationData,
          lat,
          lng,
          googleAddress,
          isFirstChange: true,
        },
      }));
    } catch (_) {
      toast.error("Adres alınamadı.");
    } finally {
      setIsLoading(false);
      setIsBusy(false);
      mapRef.current?.setOptions({
        draggable: true,
        gestureHandling: "greedy",
        scrollwheel: true,
      });
    }
  }, [mapRef, resolveAddress, setSearchText, setUpdatedLocationItem]);

  const handleUserLocationClick = () => {
    if (!("geolocation" in navigator)) {
      toast.error("Tarayıcı konum API'sini desteklemiyor.");
      return;
    }
    if (!window.confirm("Konumunuzu paylaşmak istiyor musunuz?")) return;

    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        const lat = coords.latitude;
        const lng = coords.longitude;
        try {
          setIsBusy(true);
          mapRef.current?.setOptions({
            draggable: false,
            gestureHandling: "none",
            scrollwheel: false,
          });
          if (mapRef.current) smoothPanAndZoom({ lat, lng }, 15, 200, mapRef);

          const googleAddress = await resolveAddress(lat, lng);
          setSearchText(googleAddress);
          setUpdatedLocationItem((current) => ({
            ...current,
            locationData: {
              ...current.locationData,
              lat,
              lng,
              googleAddress,
            },
          }));
        } catch (_) {
          toast.error("Adres alınamadı.");
        } finally {
          setIsBusy(false);
          mapRef.current?.setOptions({
            draggable: true,
            gestureHandling: "greedy",
            scrollwheel: true,
          });
        }
      },
      () => {
        toast.error(
          "Konum alınamadı, konumun alınmasını engelleyebilecek ayarları kontrol edip tekrar deneyin."
        );
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  return (
    <Container>
      <GoogleMap
        mapContainerClassName={`map-container ${isBusy || isLoading ? "dimmed" : ""}`}
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={currentCenter}
        zoom={5}
        mapTypeId={mapType}
        onDragEnd={onMapDragEnd}
        onLoad={onMapLoad}
        mapTypeId="hybrid"
        options={{
          zoomControl: false,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          disableDefaultUI: true,
          panControl: false,
          scrollwheel: false,
        }}
      >
        <div className="control-container-left">
          <button
            type="button"
            className="control-item"
            aria-label="Tam yakınlaştır"
            onClick={() => smoothPanAndZoom(currentCenter, 15, 200, mapRef)}
          >
            <MdOutlineKeyboardDoubleArrowDown fontSize="1.1rem" />
          </button>
          <button
            type="button"
            className="control-item"
            aria-label="Tam uzaklaştır"
            onClick={() => smoothPanAndZoom(currentCenter, 7, 200, mapRef)}
          >
            <MdOutlineKeyboardDoubleArrowUp fontSize="1.1rem" />
          </button>
        </div>

        <div className="control-container">
          <button type="button" className="control-item" aria-label="Yakınlaştır" onClick={zoomIn} disabled={isBusy}>
            <FaPlus fontSize="1.1rem" />
          </button>
          <button type="button" className="control-item" aria-label="Uzaklaştır" onClick={zoomOut} disabled={isBusy}>
            <FaMinus fontSize="1.1rem" />
          </button>
          <button type="button" className="control-item" aria-label="Konumumu kullan" onClick={handleUserLocationClick} disabled={isBusy}>
            <MdMyLocation fontSize="1.1rem" />
          </button>
        </div>

        {isBusy && (
          <div className="spinner-top">
            <SmallSpinner size="20px" color="#fff" bgColor="#E85446" />
          </div>
        )}

        <IoLocationSharp
          className="center-marker"
          fill={isBusy || !updatedLocationItem?.locationData?.googleAddress ? "#989696" : "var(--color-main)"}
        />
      </GoogleMap>
    </Container>
  );
};

export default AbsoluteLocationMap;
